import { useState, useRef, useEffect, useCallback } from "react";
import { Phone, CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { format } from "date-fns";
import { Button } from "@/components/v2/ui/button";
import { Input } from "@/components/v2/ui/input";
import { Label } from "@/components/v2/ui/label";
import { Textarea } from "@/components/v2/ui/textarea";
import { Checkbox } from "@/components/v2/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/v2/ui/radio-group";
import { Calendar } from "@/components/v2/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/v2/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/v2/ui/select";
import { cn } from "@/lib/utils";
import { BACKEND_API_ROUTE, POOJA_LEADS } from "@/commons/api_routes";
import { POSTREQUESTAPI_2 } from "@/pages/api/apiUtils";

const pujaTypes = [
  "Griha Pravesh (Housewarming)",
  "Satyanarayan Puja",
  "Ganesh Puja",
  "Navagraha Puja",
  "Mahamrityunjay Jaap",
  "Office / Business Puja",
  "Lakshmi Puja",
  "Vastu Shanti",
  "Shashtipoorthi",
  "Other (mention in notes)",
];

const EnquiryFormSection = ({ enquiryData }) => {

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    language: "",
    city: "",
    pujaType: "",
    notes: "",
  });
  const [datePreference, setDatePreference] = useState("auspicious");
  const [selectedDate, setSelectedDate] = useState(null);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const notesRef = useRef(null);

  useEffect(() => {
    if (formData.pujaType === "other-(mention-in-notes)" && notesRef.current) {
      notesRef.current.focus();
    }
  }, [formData.pujaType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!disclaimerChecked) return;

    setLoading(true);
    setError(null);

    const payload = {
      ...formData,
      datePreference,
      selectedDate: selectedDate ? format(selectedDate, "yyyy-MM-dd") : null,
      source: "homepage_v2_enquiry",
    };

    const url = `${BACKEND_API_ROUTE}${POOJA_LEADS}/crm/generic`;

    try {
      await POSTREQUESTAPI_2(url, payload, {});
      setSubmitted(true);
    } catch (err) {
      console.error("Enquiry submission error:", err);
      setError("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const isOther = formData.pujaType === "other-(mention-in-notes)";

  // Scroll to the success section when form is submitted
  useEffect(() => {
    if (submitted) {
      // Use requestAnimationFrame to wait for the DOM to update with success message
      requestAnimationFrame(() => {
        scrollToSection("enquiry", 600);
      });
    }
  }, [submitted]);

  if (submitted) {
    return (
      <section id="enquiry" className="py-10 md:py-16">
        <div className="container mx-auto max-w-2xl">
          <div className="bg-background rounded-2xl border shadow-xl p-6 md:p-10 text-center space-y-6">
            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <h2 className="text-3xl font-bold">{enquiryData?.success?.title || "Request Received!"}</h2>
            <p className="text-muted-foreground text-lg">
              {enquiryData?.success?.message || "Thank you for reaching out. Our Puja Seva Team will call you back within 10 minutes to help you with your requirements."}
            </p>
            <Button 
              variant="outline" 
              onClick={() => setSubmitted(false)}
              className="mt-4"
            >
              {enquiryData?.success?.buttonText || "Submit Another Enquiry"}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="enquiry" className="py-10 md:py-16">
      <div className="container mx-auto max-w-2xl">
        <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-serif text-primary">
              {enquiryData?.title || "Still Have Questions?"}
            </h2>
            <p className="text-lg text-muted-foreground font-sans">
              {enquiryData?.subtitle || "Fill out the form below and our experts will get back to you shortly."}
            </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-background rounded-2xl border shadow-xl p-6 md:p-10 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-sans font-semibold">Your Name</Label>
              <Input 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your name" 
                className="min-h-[44px]" 
              />
            </div>
            <div className="space-y-2">
              <Label className="font-sans font-semibold">Phone Number</Label>
              <Input 
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                type="tel"
                placeholder="+91 XXXXX XXXXX" 
                className="min-h-[44px]" 
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-sans font-semibold">Language Preference</Label>
              <Select onValueChange={(val) => handleSelectChange("language", val)}>
                <SelectTrigger className="min-h-[44px]">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {["Hindi", "Kannada", "Telugu", "Tamil", "Marathi", "Malayalam", "Gujarati", "Bengali"].map((l) => (
                    <SelectItem key={l} value={l.toLowerCase()}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-sans font-semibold">Your City</Label>
              <Input 
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                placeholder="e.g. Bangalore, Mumbai" 
                className="min-h-[44px]" 
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-sans font-semibold">Puja Type</Label>
            <Select onValueChange={(val) => handleSelectChange("pujaType", val)}>
              <SelectTrigger className="min-h-[44px]">
                <SelectValue placeholder="Select puja type" />
              </SelectTrigger>
              <SelectContent>
                {pujaTypes.map((p) => (
                  <SelectItem key={p} value={p.toLowerCase().replace(/\s+/g, "-")}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label className="font-sans font-semibold">Date Preference</Label>
            <RadioGroup value={datePreference} onValueChange={setDatePreference} className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="specific" id="specific" />
                <Label htmlFor="specific" className="cursor-pointer font-sans">📅 Yes, I want to select a specific date</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="auspicious" id="auspicious" />
                <Label htmlFor="auspicious" className="cursor-pointer font-sans">🌟 No, I need help finding the most auspicious date</Label>
              </div>
            </RadioGroup>

            {datePreference === "specific" && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    type="button"
                    className={cn(
                      "w-full justify-start text-left font-normal min-h-[44px]",
                      !selectedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  align="center"
                  sideOffset={8}
                  avoidCollisions={true}
                >
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date()}
                    className="rounded-md border"
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>

          <div className="space-y-2">
            <Label className="font-sans font-semibold">
              Additional Information <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              ref={notesRef}
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder={isOther
                ? "Please describe the puja you need help with, along with any specific requirements"
                : "Any specific requirements, number of people, special rituals, etc."
              }
              className={cn(
                "min-h-[80px] transition-all",
                isOther && "ring-2 ring-primary/50 border-primary"
              )}
            />
          </div>

          <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4 flex items-start gap-3">
            <Checkbox
              id="disclaimer"
              checked={disclaimerChecked}
              onCheckedChange={(checked) => setDisclaimerChecked(checked === true)}
              className="mt-0.5"
            />
            <Label htmlFor="disclaimer" className="cursor-pointer font-sans text-sm leading-relaxed text-foreground">
              I understand this is an enquiry and not a booking confirmation. A coordinator will call to confirm details.
            </Label>
          </div>

          {error && <p className="text-destructive text-sm font-medium">{error}</p>}

          <Button
            type="submit"
            size="lg"
            className="w-full text-base min-h-[52px] font-semibold"
            disabled={!disclaimerChecked || loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Phone className="h-5 w-5 mr-2" />
                {enquiryData?.buttonText || "Request a Callback"}
              </>
            )}
          </Button>
          <p className="text-center text-sm text-muted-foreground">{enquiryData?.footerNote || "You will get a call back within 10 minutes · Complete Stress Free Experience, Guaranteed"}</p>
        </form>
      </div>
    </section>
  );
};

export default EnquiryFormSection;
